import { useState, useCallback } from 'react';
import type { Address, Driver } from '../types';
import {
    fetchActiveDeliveryOptionsFromDb,
    insertDeliveryOptionsInDb,
    archiveDeliveryOptionInDb,
    fetchFreeDeliveryConfigFromDb,
    upsertFreeDeliveryConfigInDb,
    toggleFreeDeliveryInDb,
    fetchActiveDriverDeliveryDestinationsFromDb,
    type DeliveryOption,
    type FreeDeliveryConfig,
    type ActiveDriverDeliveryDestination,
} from '../services/deliveryService';
import { getErrorMessage } from '../utils/errors';

const MAX_MULTI_DROP_KM = 20;

function haversineKm(lat1: number, lng1: number, lat2: number, lng2: number): number {
    const R = 6371;
    const dLat = ((lat2 - lat1) * Math.PI) / 180;
    const dLng = ((lng2 - lng1) * Math.PI) / 180;
    const a =
        Math.sin(dLat / 2) ** 2 +
        Math.cos((lat1 * Math.PI) / 180) *
        Math.cos((lat2 * Math.PI) / 180) *
        Math.sin(dLng / 2) ** 2;
    return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
}

export type DriverDistanceInfo = {
    distanceKm: number | null;
    tooFar: boolean;
    activeDeliveryStatus: string | null;
};

export function useDelivery() {
    const [options,            setOptions]            = useState<DeliveryOption[]>([]);
    const [freeDeliveryConfig, setFreeDeliveryConfig] = useState<FreeDeliveryConfig | null>(null);
    const [driverDistances,    setDriverDistances]    = useState<Record<string, DriverDistanceInfo>>({});
    const [loading,            setLoading]            = useState(false);
    const [error,              setError]              = useState<string | null>(null);

    const fetchOptions = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            setOptions(await fetchActiveDeliveryOptionsFromDb());
        } catch (err) {
            setError(getErrorMessage(err, 'Failed to fetch delivery options'));
        } finally {
            setLoading(false);
        }
    }, []);

    const fetchFreeDeliveryConfig = useCallback(async () => {
        try {
            setFreeDeliveryConfig(await fetchFreeDeliveryConfigFromDb());
        } catch (err) {
            setError(getErrorMessage(err, 'Failed to fetch free delivery config'));
        }
    }, []);

    const fetchAll = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const [opts, config] = await Promise.all([
                fetchActiveDeliveryOptionsFromDb(),
                fetchFreeDeliveryConfigFromDb(),
            ]);
            setOptions(opts);
            setFreeDeliveryConfig(config);
        } catch (err) {
            setError(getErrorMessage(err, 'Failed to load delivery options'));
        } finally {
            setLoading(false);
        }
    }, []);

    const updateFees = useCallback(async (
        newPrices: { type: string; amount: number; valid_from: string }[]
    ): Promise<void> => {
        setError(null);
        await insertDeliveryOptionsInDb(newPrices);
        await fetchOptions();
    }, [fetchOptions]);

    const createOption = useCallback(async (
        type: string,
        amount: number,
        valid_to?: string
    ): Promise<void> => {
        setError(null);
        const payload: { type: string; amount: number; valid_from: string; valid_to?: string } = {
            type,
            amount,
            valid_from: new Date().toISOString(),
        };
        if (valid_to) payload.valid_to = valid_to;
        await insertDeliveryOptionsInDb([payload]);
        await fetchOptions();
    }, [fetchOptions]);

    const archiveOption = useCallback(async (id: string): Promise<void> => {
        setError(null);
        await archiveDeliveryOptionInDb(id);
        setOptions(prev => prev.filter(o => o.id !== id));
    }, []);

    const saveFreeDeliveryThreshold = useCallback(async (
        threshold_cents: number
    ): Promise<void> => {
        setError(null);
        await upsertFreeDeliveryConfigInDb(freeDeliveryConfig?.id ?? null, threshold_cents);
        await fetchFreeDeliveryConfig();
    }, [freeDeliveryConfig, fetchFreeDeliveryConfig]);

    const toggleFreeDelivery = useCallback(async (): Promise<void> => {
        if (!freeDeliveryConfig) return;
        setError(null);
        await toggleFreeDeliveryInDb(freeDeliveryConfig.id, !freeDeliveryConfig.is_active);
        setFreeDeliveryConfig(prev => prev ? { ...prev, is_active: !prev.is_active } : prev);
    }, [freeDeliveryConfig]);

    const fetchDriverDistancesForDestination = useCallback(async (
        drivers: Driver[],
        destination: Pick<Address, 'latitude' | 'longitude'>
    ): Promise<Record<string, DriverDistanceInfo>> => {
        setError(null);
        try {
            const activeDeliveries = await fetchActiveDriverDeliveryDestinationsFromDb(drivers);
            const distances: Record<string, DriverDistanceInfo> = {};

            for (const driver of drivers) {
                const activeDelivery = activeDeliveries.find(
                    (delivery: ActiveDriverDeliveryDestination) => delivery.driver_id === driver.auth_id
                );

                if (!activeDelivery) {
                    distances[driver.auth_id] = { distanceKm: null, tooFar: false, activeDeliveryStatus: null };
                    continue;
                }

                const activeDestination = activeDelivery.addresses;

                if (!activeDestination?.latitude || !activeDestination?.longitude) {
                    distances[driver.auth_id] = { distanceKm: null, tooFar: false, activeDeliveryStatus: activeDelivery.status };
                    continue;
                }

                const distanceKm = haversineKm(
                    Number(activeDestination.latitude),
                    Number(activeDestination.longitude),
                    Number(destination.latitude),
                    Number(destination.longitude),
                );

                distances[driver.auth_id] = {
                    distanceKm,
                    tooFar: distanceKm > MAX_MULTI_DROP_KM,
                    activeDeliveryStatus: activeDelivery.status,
                };
            }

            setDriverDistances(distances);
            return distances;
        } catch (err) {
            setError(getErrorMessage(err, 'Failed to fetch driver delivery distances'));
            setDriverDistances({});
            return {};
        }
    }, []);

    const clearDriverDistances = useCallback(() => {
        setDriverDistances({});
    }, []);

    return {
        options,
        freeDeliveryConfig,
        driverDistances,
        loading,
        error,
        maxMultiDropKm: MAX_MULTI_DROP_KM,
        fetchAll,
        fetchOptions,
        fetchFreeDeliveryConfig,
        updateFees,
        createOption,
        archiveOption,
        saveFreeDeliveryThreshold,
        toggleFreeDelivery,
        fetchDriverDistancesForDestination,
        clearDriverDistances,
    };
}